import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getEnrollments,
    enrollStudent,
    deleteEnrollment,
    type EnrollmentData,
} from '../lib/api';
import { queryKeys } from '../lib/queryClient';

export function useEnrollments(token: string | null, schoolProfileId?: string) {
    return useQuery({
        queryKey: queryKeys.enrollments.list(schoolProfileId),
        queryFn: () => getEnrollments(token!, schoolProfileId),
        enabled: !!token && !!schoolProfileId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useEnrollStudent(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ classId, studentId, schoolProfileId: profileId }: {
            classId: string;
            studentId: string;
            schoolProfileId?: string
        }) => enrollStudent(classId, studentId, token!, profileId || schoolProfileId),

        onMutate: async ({ classId, studentId }) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.enrollments.list(schoolProfileId) });
            const previousEnrollments = queryClient.getQueryData<EnrollmentData[]>(queryKeys.enrollments.list(schoolProfileId));

            const tempId = `temp-${Date.now()}`;
            queryClient.setQueryData<EnrollmentData[]>(
                queryKeys.enrollments.list(schoolProfileId),
                (old = []) => [...old, {
                    id: tempId,
                    class_id: classId,
                    student_id: studentId,
                    status: 'active',
                    school_profile_id: schoolProfileId,
                } as EnrollmentData]
            );

            return { previousEnrollments, tempId };
        },

        onSuccess: (createdEnrollment, _variables, context) => {
            queryClient.setQueryData<EnrollmentData[]>(
                queryKeys.enrollments.list(schoolProfileId),
                (old = []) => old.map(e => e.id === context?.tempId ? createdEnrollment : e)
            );
        },

        onError: (_err, _variables, context) => {
            if (context?.previousEnrollments) {
                queryClient.setQueryData(queryKeys.enrollments.list(schoolProfileId), context.previousEnrollments);
            }
        },
    });
}

export function useDeleteEnrollment(token: string | null, schoolProfileId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteEnrollment(id, token!),

        onMutate: async (deletedId) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.enrollments.list(schoolProfileId) });
            const previousEnrollments = queryClient.getQueryData<EnrollmentData[]>(queryKeys.enrollments.list(schoolProfileId));

            queryClient.setQueryData<EnrollmentData[]>(
                queryKeys.enrollments.list(schoolProfileId),
                (old = []) => old.filter(e => e.id !== deletedId)
            );

            return { previousEnrollments };
        },

        onError: (_err, _deletedId, context) => {
            if (context?.previousEnrollments) {
                queryClient.setQueryData(queryKeys.enrollments.list(schoolProfileId), context.previousEnrollments);
            }
        },
    });
}
